import React, {useState} from 'react';
import {gql, useMutation, useQuery} from '@apollo/client';
import {Box, Grid, Typography} from '@mui/material';
import moment from 'moment';
import {useSnackbar} from 'notistack';
import ActionModal from './ActionModal';
import Loading from '../Loading/Loading';
import {
  AdvisorCreateFactFindSession,
  AdvisorCreateFactFindSessionVariables,
} from '../../schema-types/AdvisorCreateFactFindSession';
import {
  SendClientFactFind,
  SendClientFactFindVariables,
} from '../../schema-types/SendClientFactFind';
import {GetFactFind, GetFactFindVariables} from '../../schema-types/GetFactFind';
import {CreateFactFind, CreateFactFindVariables} from '../../schema-types/CreateFactFind';
import {GetFactFindTemplates} from '../../schema-types/GetFactFindTemplates';

import validateUUID from '../../utils/validateUUID';
import {ContactSearchFragment} from '../../schema-types/ContactSearchFragment';
import {getContactName} from '../../utils/getContactName';
import {
  FinCalcSessionTypeEnum,
  GlobalPermissionEnum,
} from '../../schema-types/globalTypes';
import {SelectFactFindTemplate} from './SelectFactFindTemplate';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import {AccountBasicFragment} from '../../schema-types/AccountBasicFragment';
import {SaltusAny} from '../../utils/SaltusAny';
import {DeleteFactFind, DeleteFactFindVariables} from '../../schema-types/DeleteFactFind';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';
import {ScrollableModal} from '../Common/ScrollableModal';
import {Button} from '../Button/Button';

type FactFindProps = {
  contact: ContactSearchFragment;
  isOpen: boolean;
  account: AccountBasicFragment;
  close: () => SaltusAny;
};

type propsWithPrevious = FactFindProps & {
  factFindData: GetFactFind;
};

const getFactFindQuery = gql`
  query GetFactFind($contactId: UUID!) {
    getFactFind(contactId: $contactId) {
      pagesCompleted
      status
      lastSeen
      isSubmitted
    }
  }
`;
export const createFactFindMutation = gql`
  mutation CreateFactFind($templateId: UUID!, $contactId: UUID!) {
    createFactFind(contactId: $contactId, templateId: $templateId) {
      ok
      contactId
      errors
    }
  }
`;

const sendClientFactFindMutation = gql`
  mutation SendClientFactFind($accountId: UUID!, $contactId: UUID!) {
    submitFactFind(accountId: $accountId, contactId: $contactId) {
      ok
      contactId
      errors
    }
  }
`;

const createFactFindSessionMutation = gql`
  mutation AdvisorCreateFactFindSession(
    $clientId: UUID
    $contactId: UUID!
    $accountId: UUID!
    $sessionType: FinCalcSessionTypeEnum
  ) {
    createFactFindSession(
      clientId: $clientId
      contactId: $contactId
      sessionType: $sessionType
      accountId: $accountId
    ) {
      ok
      sessionUrl
    }
  }
`;

export const checkFactFindPermissionMutation = gql`
  mutation CheckFactFindPermission(
    $staffId: UUID!
    $contactId: UUID!
    $templateId: UUID!
  ) {
    factFindCheckPermission(
      staffId: $staffId
      contactId: $contactId
      templateId: $templateId
    ) {
      ok
      errors
    }
  }
`;

export const getFactFindTemplates = gql`
  query GetFactFindTemplates {
    fincalcTemplates(apiSync: true) {
      id
      isDeleted
      isDummy
      dateDeleted
      dateCreated
      dateUpdated
      fincalcId
      name
    }
  }
`;

export const deleteFactFind = gql`
  mutation DeleteFactFind($contactId: UUID, $deletePortalNotification: Boolean) {
    deleteFactFind(
      contactId: $contactId
      deletePortalNotification: $deletePortalNotification
    ) {
      ok
      errors
    }
  }
`;

const FactFindModal: React.FC<FactFindProps> = ({contact, isOpen, close, account}) => {
  const {data, loading, error} = useQuery<GetFactFind, GetFactFindVariables>(
    getFactFindQuery,
    {
      variables: {
        contactId: contact.id,
      },
    }
  );

  if (!validateUUID(contact.id)) {
    return null;
  }

  return (
    <ActionModal
      title="Factfind Questionnaire"
      isOpen={isOpen}
      close={close}
      wide
      autoHeight
    >
      <Box mb={2}>
        {loading && <Loading />}
        {error && <ErrorMessage error={error} />}
        {data && (
          <FactFind
            account={account}
            close={close}
            isOpen={isOpen}
            contact={contact}
            factFindData={data}
          />
        )}
      </Box>
    </ActionModal>
  );
};

const FactFind: React.FC<propsWithPrevious> = ({
  contact,
  close,
  isOpen,
  account,
  factFindData,
}) => {
  return (
    <>
      {factFindData.getFactFind ? (
        <>
          <FactFindPrevious
            account={account}
            close={close}
            isOpen={isOpen}
            contact={contact}
            factFindData={factFindData}
          />
          <UpdateFactFind
            account={account}
            close={close}
            isOpen={isOpen}
            contact={contact}
            factFindData={factFindData}
          />
        </>
      ) : (
        <CreateFactFindDisplay
          account={account}
          close={close}
          isOpen={isOpen}
          contact={contact}
          factFindData={factFindData}
        />
      )}
    </>
  );
};

const CreateFactFindDisplay: React.FC<propsWithPrevious> = ({
  account,
  close,
  contact,
  factFindData,
  isOpen,
}) => {
  const {enqueueSnackbar} = useSnackbar();
  const [templateId, setTemplateId] = useState<string | null>(null);
  const {data: templatesData, loading: templatesLoading} =
    useQuery<GetFactFindTemplates>(getFactFindTemplates);
  const [onCreateFactFind, {loading: onClientSendFactFindLoading}] = useMutation<
    CreateFactFind,
    CreateFactFindVariables
  >(createFactFindMutation);

  if (!templatesData?.fincalcTemplates || templatesLoading) {
    return <Loading />;
  }
  return (
    <>
      <Box mt={4}>
        <Grid container spacing={4} direction="column">
          <Grid item xs={12}>
            {templatesData.fincalcTemplates?.length ? (
              <Box alignItems="center" justifyContent="flex-end" display="flex">
                <SelectFactFindTemplate
                  selectedTemplateId={templateId}
                  setSelectedTemplateId={setTemplateId}
                  templatesData={templatesData}
                />
              </Box>
            ) : (
              <Typography>No Fact Find Templates to display</Typography>
            )}
          </Grid>
          <Grid item xs={12} style={{display: 'flex'}}>
            <Button
              eventName="Create Fact Find"
              onClick={() => {
                if (!templateId || !validateUUID(contact.id)) {
                  return;
                }

                onCreateFactFind({
                  variables: {contactId: contact.id, templateId},
                  refetchQueries: [
                    {
                      query: getFactFindQuery,
                      variables: {contactId: contact.id},
                    },
                  ],
                }).then(({data}) => {
                  if (data?.createFactFind?.ok) {
                    enqueueSnackbar('Created fact find', {
                      variant: 'success',
                    });
                  } else {
                    let errorMessage = 'Unable to create fact find';
                    // Acquire a descriptive error if present
                    if (
                      data?.createFactFind?.errors &&
                      data.createFactFind.errors.length > 0
                    ) {
                      errorMessage = `${errorMessage}. ${data.createFactFind.errors[0]}`;
                    }
                    enqueueSnackbar(errorMessage, {
                      variant: 'error',
                      autoHideDuration: null,
                    });
                  }
                });
              }}
              variant="contained"
              color="primary"
              disabled={onClientSendFactFindLoading || !templateId}
            >
              {onClientSendFactFindLoading
                ? 'Creating fact find...'
                : `Start fact find for ${getContactName(contact)}`}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
const UpdateFactFind: React.FC<propsWithPrevious> = ({
  account,
  close,
  contact,
  factFindData,
  isOpen,
}) => {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const {enqueueSnackbar} = useSnackbar();
  const [onClientSendFactFind, {loading: onClientSendFactFindLoading}] = useMutation<
    SendClientFactFind,
    SendClientFactFindVariables
  >(sendClientFactFindMutation, {
    variables: {contactId: contact.id, accountId: account.id},
  });
  const [onDeleteFactFind, {loading: onDeleteFactFindLoading}] = useMutation<
    DeleteFactFind,
    DeleteFactFindVariables
  >(deleteFactFind, {
    variables: {contactId: contact.id, deletePortalNotification: true},
    refetchQueries: [{query: getFactFindQuery, variables: {contactId: contact.id}}],
    awaitRefetchQueries: true,
  });
  const [
    onAdvisorCreateFactFindSession,
    {loading: onAdvisorCreateFactFindSessionLoading},
  ] = useMutation<AdvisorCreateFactFindSession, AdvisorCreateFactFindSessionVariables>(
    createFactFindSessionMutation,
    {
      variables: {
        contactId: contact.id,
        accountId: account.id,
        sessionType: FinCalcSessionTypeEnum.ADVISER,
      },
    }
  );
  const [onPrefillFactFindSession, {loading: onPrefillFactFindSessionLoading}] =
    useMutation<AdvisorCreateFactFindSession, AdvisorCreateFactFindSessionVariables>(
      createFactFindSessionMutation,
      {
        variables: {
          contactId: contact.id,
          accountId: account.id,
          sessionType: FinCalcSessionTypeEnum.PREFILL,
        },
      }
    );

  if (!validateUUID(contact.id)) {
    return null;
  }

  return (
    <>
      <ScrollableModal
        size="small"
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
      >
        <>
          <Typography mb={1}>Are you sure you want to delete this fact find?</Typography>
          <Box display="flex" justifyContent="space-between">
            <Button
              eventName={null}
              onClick={() => {
                setConfirmDeleteOpen(false);
              }}
              disabled={onDeleteFactFindLoading}
              variant="contained"
              color="primary"
            >
              Cancel
            </Button>
            <Button
              eventName="Confirm delete Fact Find"
              onClick={() => {
                onDeleteFactFind().then(({data}) => {
                  if (data?.deleteFactFind?.ok) {
                    enqueueSnackbar('Deleted fact find', {
                      variant: 'success',
                    });
                  } else {
                    enqueueSnackbar(`Unable to delete fact find session.`, {
                      variant: 'error',
                      autoHideDuration: null,
                    });
                  }
                  close();
                });
              }}
              disabled={onDeleteFactFindLoading}
              variant="contained"
              color="secondary"
            >
              Confirm delete
            </Button>
          </Box>
        </>
      </ScrollableModal>
      <Box display="flex" justifyContent="space-between" alignContent="center">
        <Button
          eventName="Send client questionnaire"
          onClick={() =>
            onClientSendFactFind().then(({data}) => {
              if (data?.submitFactFind?.ok) {
                enqueueSnackbar('Sent fact find questionnaire to client', {
                  variant: 'success',
                });
                close();
              } else {
                enqueueSnackbar('Unable to send fact find questionnaire', {
                  variant: 'error',
                  autoHideDuration: null,
                });
              }
            })
          }
          variant="contained"
          color="primary"
          disabled={onClientSendFactFindLoading}
        >
          Send client questionnaire
        </Button>

        <Button
          eventName="Open fact find"
          onClick={() =>
            onAdvisorCreateFactFindSession().then(({data}) => {
              const url = data?.createFactFindSession?.sessionUrl;
              if (url) {
                window.open(url, '_blank');
              } else {
                enqueueSnackbar('Unable to create fact find session', {
                  variant: 'error',
                  autoHideDuration: null,
                });
              }
            })
          }
          disabled={onAdvisorCreateFactFindSessionLoading}
          variant="contained"
          color="primary"
        >
          Open fact find
        </Button>
        <FeatureBoundary
          features={[GlobalPermissionEnum.can_access_admin_features]}
          showErrorIfDenied={false}
        >
          <Button
            eventName="Delete fact find"
            sx={{ml: 1}}
            onClick={() => {
              setConfirmDeleteOpen(true);
            }}
            variant="contained"
            color="secondary"
          >
            Delete fact find
          </Button>
        </FeatureBoundary>

        <Button
          eventName="Pre-fill"
          onClick={() =>
            onPrefillFactFindSession().then(({data}) => {
              const url = data?.createFactFindSession?.sessionUrl;
              if (url) {
                window.open(url, '_blank');
              } else {
                enqueueSnackbar('Unable to prefill fact find session', {
                  variant: 'error',
                  autoHideDuration: null,
                });
              }
            })
          }
          variant="contained"
          color="primary"
          disabled={onPrefillFactFindSessionLoading}
        >
          Pre-fill
        </Button>
      </Box>
    </>
  );
};

const FactFindPrevious: React.FC<propsWithPrevious> = ({
  contact,
  close,
  isOpen,
  factFindData,
  account,
}) => {
  if (!factFindData.getFactFind) {
    return (
      <Box mb={4} mt={2}>
        <Typography variant="h5">No previous fact find</Typography>
      </Box>
    );
  }
  return (
    <>
      <Box mb={4} mt={2}>
        <Typography variant="h5">Current fact find:</Typography>
        <Box mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={3} alignItems="center" justifyContent="center">
              <strong>Submitted: </strong>
              <br />
              {factFindData.getFactFind.isSubmitted ? 'Submitted' : 'Not submitted'}
              <br /> {factFindData.getFactFind.isSubmitted}
            </Grid>
            <Grid item xs={3} alignItems="center" justifyContent="center">
              <strong>Client status: </strong>
              <br />
              {factFindData.getFactFind.status}
            </Grid>
            <Grid item xs={3} alignItems="center" justifyContent="center">
              <strong>Last seen</strong>:<br />{' '}
              {moment.utc(factFindData.getFactFind.lastSeen).format('DD/MM/YYYY HH:MM')}
            </Grid>
            <Grid item xs={3} alignItems="center" justifyContent="center">
              <strong>Pages completed:</strong>
              <br /> {factFindData.getFactFind.pagesCompleted}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};
export default FactFindModal;
